/**
 * GeneralInput Component
 *
 * A reusable input component integrated with Formik.
 * Supports text, password, textarea, and URL input types, with extensive customizations.
 *
 * Props:
 * @param {string} type - Input type: "text", "password", "textarea", "url", etc. Default: "text".
 * @param {string} name - Formik field name.
 * @param {string} label - Input label.
 * @param {string} placeholder - Placeholder text.
 * @param {boolean} required - Whether the field is required. Default: false.
 * @param {boolean} numericOnly - Allow only numeric input. Default: false.
 * @param {boolean} englishOnly - Allow only English letters and numbers. Default: false.
 * @param {boolean} arabicOnly - Allow only Arabic letters and numbers. Default: false.
 * @param {(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void} onChange - Optional onChange handler.
 * @param {(e: React.KeyboardEvent<HTMLInputElement>) => void} onKeyDown - Optional onKeyDown handler.
 * @param {string} wrapperClassName - Extra CSS classes for the wrapper container.
 * @param {string | number} defaultValue - Default value to initialize the input.
 * @param {boolean} priceInput - Whether this input represents a price (adds currency symbol).
 * @param {boolean} disabled - Disable the input. Default: false.
 * @param {number} maxLength - Maximum number of characters.
 * @param {number} maxValue - Maximum numeric value allowed.
 * @param {number} minValue - Minimum numeric value allowed.
 * @param {string} extraTextareaClassName - Extra CSS classes for textarea.
 * @param {string} extraInputClassName - Extra CSS classes for input.
 * @param {string} labelClassName - Extra CSS classes for label.
 * @param {boolean} resetOnchangeLanguage - Reset value on language change. Default: false.
 * @param {"rtl" | "ltr"} inputDirection - Direction of text input.
 * @param {boolean} autoFocus - Autofocus the input.
 * @param {(e: React.WheelEvent<HTMLInputElement>) => void} onWheel - Optional wheel event handler.
 *
 * Behavior:
 * - Supports controlled Formik integration.
 * - Automatically handles numeric-only, English-only, and Arabic-only constraints.
 * - Shows/hides password input when type="password".
 * - Can render as textarea, URL input, or standard input field.
 * - Supports max/min values, maxLength, disabled state, and autoFocus.
 * - Adds currency label for price input fields based on current language.
 * - Resets value on language change if `resetOnchangeLanguage` is true.
 *
 * Usage:
 * ```tsx
 * <Formik initialValues={{ username: "" }} onSubmit={...}>
 *   {() => (
 *     <Form>
 *       <GeneralInput
 *         type="text"
 *         name="username"
 *         label="Username"
 *         placeholder="Enter your username"
 *         required
 *       />
 *       <GeneralInput
 *         type="password"
 *         name="password"
 *         label="Password"
 *         placeholder="Enter your password"
 *       />
 *       <GeneralInput
 *         type="textarea"
 *         name="description"
 *         label="Description"
 *       />
 *     </Form>
 *   )}
 * </Formik>
 * ```
 *
 * Notes:
 * - Uses `BASE_LOCAL_IMAGES_DIR` for eye toggle icons.
 * - Fully compatible with RTL/LTR layouts.
 * - Provides inline validation messages from Formik.
 */

import { Field, FieldProps, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GeneralInputProps } from "../../types/GeneralComponents";
import {
  ArabicCode,
  BASE_LOCAL_IMAGES_DIR,
  GlobalSyrianCurrentLabelAr,
  GlobalSyrianCurrentLabelEn,
  urlRegex,
} from "../../utils/constants/CommonConstants";
import { closedEye_IMG, eye_IMG } from "../../utils/constants/Images";

const GeneralInput: React.FC<GeneralInputProps> = ({
  type = "text",
  name,
  label,
  placeholder = "",
  required = false,
  numericOnly = false,
  englishOnly = false,
  arabicOnly = false,
  onChange,
  onKeyDown,
  wrapperClassName,
  defaultValue,
  priceInput,
  disabled,
  maxLength,
  maxValue,
  extraTextareaClassName,
  extraInputClassName,
  labelClassName,
  resetOnchangeLanguage = false,
  inputDirection,
  autoFocus,
  onWheel,
  minValue,
}) => {
  const { setFieldValue, setErrors, validateForm, resetForm } =
    useFormikContext();
  const { i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  //?==================Webiste Input Handler =================

  const handleWebsiteInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: FieldProps["field"],
  ) => {
    const inputValue = e.target.value.trim();

    field.onChange(e);

    if (urlRegex.test(inputValue) || inputValue === "") {
      if (onChange) onChange(e);
    }
  };

  useEffect(() => {
    if (resetOnchangeLanguage) {
      const handleLanguageChange = () => {
        setFieldValue(name, "");
        setErrors({});
        validateForm({});
        resetForm();
      };

      i18n.on("languageChanged", handleLanguageChange);

      return () => {
        i18n.off("languageChanged", handleLanguageChange);
      };
    }
  }, [i18n, setErrors, setFieldValue, validateForm, resetOnchangeLanguage]);

  //?==================Input Handler =================
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: FieldProps["field"],
  ) => {
    let { value } = e.target;

    //?=== Allow only digits
    if (numericOnly) {
      value = value.replace(/[^\d]/g, "");
    }
    //?=== Allow only English characters
    if (englishOnly) {
      value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    }
    //?=== Allow only Arabic characters
    if (arabicOnly) {
      value = value.replace(/[^\u0600-\u06FF0-9\s]/g, "");
    }

    //?=== Set the controlled input value
    setFieldValue(name, value);

    //?=== Call onChange if provided
    if (onChange) {
      const newEvent = {
        ...e,
        target: { ...e.target, value },
      };
      onChange(newEvent);
    }
  };

  const getPlaceholderWithAsterisk = () => {
    return required ? `${placeholder} *` : placeholder;
  };

  const isTextarea = type === "textarea";
  const inputType = type === "password" && !showPassword ? "password" : "text";

  useEffect(() => {
    if (defaultValue !== undefined) {
      setFieldValue(name, defaultValue);
    }
  }, [defaultValue]);

  return (
    <div className={`relative ${wrapperClassName}`}>
      {label && (
        <label
          htmlFor={name}
          className={`absolute -top-2 left-2 z-10 bg-white px-1 text-sm transition-all duration-200 ${labelClassName} ${
            i18n.dir() === "rtl" ? "right-2" : "left-2"
          }`}
        >
          {label}
        </label>
      )}
      <Field name={name}>
        {({ field, meta }: FieldProps) => {
          return (
            <>
              {isTextarea ? (
                <textarea
                  id={name}
                  className={`focus:ring-primary w-full resize-none rounded-lg bg-inputBg px-4 py-2 text-base text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 ${extraTextareaClassName}`}
                  placeholder={getPlaceholderWithAsterisk()}
                  value={field.value}
                  onChange={(e) => handleInputChange(e, field)}
                  disabled={disabled}
                  rows={4}
                  maxLength={maxLength}
                  autoFocus={autoFocus}
                />
              ) : type === "url" ? (
                <div className="relative flex w-full items-center">
                  <input
                    type="text"
                    id={name}
                    className={`focus:ring-primary w-full rounded-lg bg-inputBg px-4 py-4 text-base text-gray-700 placeholder-gray-400 shadow-login_input focus:outline-none focus:ring-2 ${extraInputClassName}`}
                    placeholder={getPlaceholderWithAsterisk()}
                    value={field.value}
                    onChange={(e) => handleWebsiteInputChange(e, field)}
                    disabled={disabled}
                    onKeyDown={onKeyDown}
                    autoFocus={autoFocus}
                    dir={inputDirection}
                  />
                  {priceInput ? (
                    <div className="absolute right-8 text-textColor">
                      {i18n.language === ArabicCode
                        ? GlobalSyrianCurrentLabelAr
                        : GlobalSyrianCurrentLabelEn}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="relative flex w-full items-center">
                  <input
                    type={type === "password" ? inputType : type}
                    id={name}
                    className={`w-full rounded-lg bg-inputBg px-4 py-4 text-base text-headingTextColor placeholder-gray-400 shadow-login_input focus:outline-primaryMain ${extraInputClassName}`}
                    placeholder={getPlaceholderWithAsterisk()}
                    value={field.value}
                    onChange={(e) => handleInputChange(e, field)}
                    disabled={disabled}
                    onKeyDown={onKeyDown}
                    autoFocus={autoFocus}
                    max={maxValue}
                    min={minValue}
                    onWheel={onWheel}
                  />
                  {/* //?=======Price Handling========= */}
                  {priceInput ? (
                    <div
                      className={`absolute text-lg text-textColor ${
                        i18n.language === ArabicCode ? "left-8" : "right-8"
                      }`}
                    >
                      {i18n.language === ArabicCode
                        ? GlobalSyrianCurrentLabelAr
                        : GlobalSyrianCurrentLabelEn}
                    </div>
                  ) : null}
                </div>
              )}
              {type === "password" && (
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className={`absolute top-5 focus:outline-none ${
                    i18n.dir() === "rtl" ? "left-3" : "right-3"
                  } flex items-center pe-2.5 text-sm`}
                >
                  <img
                    src={
                      showPassword
                        ? `${BASE_LOCAL_IMAGES_DIR}/${eye_IMG}`
                        : `${BASE_LOCAL_IMAGES_DIR}/${closedEye_IMG}`
                    }
                    alt="Toggle visibility"
                    loading="lazy"
                    className="relative -top-1 h-6 w-6"
                  />
                </button>
              )}
              {meta.touched && meta.error && (
                <p className="mt-1 text-sm font-semibold text-errorMain">
                  {meta.error}
                </p>
              )}
            </>
          );
        }}
      </Field>
    </div>
  );
};

export default GeneralInput;
